import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { Lock, LogOut, Trash2, Plus, Loader2, Image as ImageIcon, X, ChevronRight, Package } from "lucide-react";
import { CATEGORIES } from "../mock";
import { invalidateProductsCache } from "../hooks/useProducts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "zeid_admin_token";

const MAX_IMAGE_SIZE_MB = 1.5; // resize > 1.5MB
const MAX_IMAGE_WIDTH = 1200;

async function fileToResizedDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_IMAGE_WIDTH) {
          height = (height * MAX_IMAGE_WIDTH) / width;
          width = MAX_IMAGE_WIDTH;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const emptyForm = {
  name: "", brand: "", category: "refrigerateur", condition: "Occasion",
  price: "", oldPrice: "", description: "", stock: 1, images: [],
};

const Admin = () => {
  const { toast } = useToast();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const authHeaders = token ? { "X-Admin-Password": token } : {};

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/products`, { headers: authHeaders });
      setProducts(data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else toast({ title: "Erreur", description: "Impossible de charger la liste.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
      toast({ title: "Connecté", description: "Bienvenue dans l'espace administrateur." });
    } catch (err) {
      toast({ title: "Accès refusé", description: err?.response?.data?.detail || "Mot de passe incorrect.", variant: "destructive" });
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setProducts([]);
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingImage(true);
    try {
      const newImages = [];
      for (const f of files) {
        try {
          const dataUrl = await fileToResizedDataUrl(f);
          newImages.push(dataUrl);
        } catch {
          // fallback: skip
        }
      }
      setForm((s) => ({ ...s, images: [...s.images, ...newImages].slice(0, 5) }));
      toast({ title: "Image ajoutée", description: `${newImages.length} photo(s) prête(s).` });
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm((s) => ({ ...s, images: s.images.filter((_, i) => i !== idx) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) {
      toast({ title: "Champs manquants", description: "Le nom et le prix sont obligatoires.", variant: "destructive" });
      return;
    }
    if (form.images.length === 0) {
      toast({ title: "Photo manquante", description: "Ajoutez au moins une photo du produit.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        oldPrice: form.oldPrice ? parseFloat(form.oldPrice) : 0,
        stock: parseInt(form.stock, 10) || 1,
      };
      await axios.post(`${API}/admin/products`, payload, { headers: authHeaders });
      toast({ title: "Produit ajouté", description: `${form.name} est maintenant en vente sur le site.` });
      setForm(emptyForm);
      invalidateProductsCache();
      fetchProducts();
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else toast({ title: "Erreur", description: err?.response?.data?.detail || "Impossible d'ajouter le produit.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce produit du site ?")) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, { headers: authHeaders });
      toast({ title: "Supprimé" });
      invalidateProductsCache();
      fetchProducts();
    } catch (err) {
      toast({ title: "Erreur", description: "Suppression impossible.", variant: "destructive" });
    }
  };

  // Non-connecté → écran de login
  if (!token) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-16 bg-neutral-100">
        <div className="w-full max-w-md bg-white rounded-2xl border border-neutral-200 shadow-xl p-8">
          <div className="flex flex-col items-center text-center">
            <div className="h-14 w-14 rounded-full bg-[#0b2960] text-white flex items-center justify-center">
              <Lock className="h-6 w-6" />
            </div>
            <h1 className="mt-4 text-2xl font-extrabold text-[#0b2960]">Espace administrateur</h1>
            <p className="text-sm text-neutral-500 mt-1">Réservé au gérant d'EDS ELECTROMENAGER.</p>
          </div>
          <form onSubmit={login} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 h-11" placeholder="••••••••" autoFocus />
            </div>
            <Button type="submit" disabled={loggingIn} className="w-full h-11 bg-[#0b2960] hover:bg-[#081e47] font-bold disabled:opacity-70">
              {loggingIn ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Connexion...</>) : "Se connecter"}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/" className="text-xs text-neutral-500 hover:text-[#0b2960] inline-flex items-center gap-1">← Retour à la boutique</Link>
          </div>
        </div>
      </div>
    );
  }

  // Écran admin
  return (
    <div className="min-h-[calc(100vh-200px)] bg-neutral-100">
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center text-sm text-neutral-600 gap-1">
          <Link to="/" className="hover:text-[#0b2960]">Accueil</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-[#0b2960] font-semibold">Espace administrateur</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">Gestion des produits</h1>
            <p className="text-sm text-neutral-500 mt-1">Ajoutez ou supprimez les produits qui apparaissent sur votre site.</p>
          </div>
          <Button onClick={logout} variant="outline" className="border-neutral-300 text-neutral-700 hover:text-[#e11d24]">
            <LogOut className="h-4 w-4 mr-2" /> Déconnexion
          </Button>
        </div>

        <div className="grid lg:grid-cols-[420px_1fr] gap-6">
          {/* Add product form */}
          <div className="bg-white rounded-xl border border-neutral-200 p-6 h-fit lg:sticky lg:top-[160px]">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-9 w-9 rounded-full bg-[#e11d24] text-white flex items-center justify-center">
                <Plus className="h-5 w-5" />
              </div>
              <h2 className="text-lg font-extrabold text-[#0b2960]">Ajouter un produit</h2>
            </div>

            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Nom du produit *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : SAMSUNG Lave-linge 9kg" className="mt-1" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Marque</Label>
                  <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Samsung, Bosch..." className="mt-1" />
                </div>
                <div>
                  <Label>État</Label>
                  <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Occasion">Occasion</SelectItem>
                      <SelectItem value="Neuf">Neuf</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Catégorie *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.filter((c) => c.slug !== "occasion").map((c) => (
                      <SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Prix TTC (€) *</Label>
                  <Input required type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="150" className="mt-1" />
                </div>
                <div>
                  <Label>Prix barré (€)</Label>
                  <Input type="number" step="0.01" min="0" value={form.oldPrice} onChange={(e) => setForm({ ...form, oldPrice: e.target.value })} placeholder="449" className="mt-1" />
                </div>
                <div>
                  <Label>Stock</Label>
                  <Input type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="mt-1" />
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="État, options, particularités..." className="mt-1" />
              </div>

              <div>
                <Label>Photos (jusqu'à 5)</Label>
                <div className="mt-2 grid grid-cols-5 gap-2">
                  {form.images.map((img, i) => (
                    <div key={i} className="relative aspect-square rounded-md overflow-hidden border border-neutral-200 group">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-[#e11d24] transition-opacity">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  {form.images.length < 5 && (
                    <label className="aspect-square rounded-md border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center gap-1 text-neutral-500 hover:border-[#0b2960] hover:text-[#0b2960] cursor-pointer transition-colors">
                      {uploadingImage ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImageIcon className="h-5 w-5" />}
                      <span className="text-[10px] text-center leading-tight">Ajouter</span>
                      <input type="file" accept="image/*" multiple onChange={handleImageChange} className="hidden" />
                    </label>
                  )}
                </div>
                <p className="text-[11px] text-neutral-500 mt-2">Formats acceptés : JPG, PNG, WebP. Les images sont automatiquement redimensionnées.</p>
              </div>

              <Button type="submit" disabled={submitting} className="w-full h-11 bg-[#e11d24] hover:bg-[#b8171d] font-bold disabled:opacity-70">
                {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Ajout en cours...</>) : (<><Plus className="h-4 w-4 mr-2" /> Ajouter le produit</>)}
              </Button>
            </form>
          </div>

          {/* Products list */}
          <div className="bg-white rounded-xl border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-full bg-[#0b2960] text-white flex items-center justify-center">
                  <Package className="h-5 w-5" />
                </div>
                <h2 className="text-lg font-extrabold text-[#0b2960]">Vos produits ajoutés</h2>
              </div>
              <div className="text-sm text-neutral-500">{products.length} produit{products.length > 1 ? "s" : ""}</div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16 text-neutral-500">
                <Loader2 className="h-6 w-6 animate-spin mr-2" /> Chargement...
              </div>
            ) : products.length === 0 ? (
              <div className="border border-dashed border-neutral-300 rounded-lg p-10 text-center text-neutral-600">
                <Package className="h-8 w-8 mx-auto mb-2 text-neutral-400" />
                Aucun produit ajouté pour le moment. Utilisez le formulaire à gauche pour commencer.
              </div>
            ) : (
              <div className="space-y-6">
                {[
                  { key: "Neuf", title: "Produits neufs", color: "green", subtitle: "Garantie constructeur" },
                  { key: "Occasion", title: "Produits d'occasion", color: "blue", subtitle: "Garantie 4 mois · reconditionnés" },
                ].map((section) => {
                  const list = products.filter((p) => p.condition === section.key);
                  return (
                    <div key={section.key} className="rounded-lg border-2 overflow-hidden" style={{ borderColor: section.color === "green" ? "#16a34a" : "#0b2960" }}>
                      <div className="flex items-center justify-between px-4 py-2.5" style={{ backgroundColor: section.color === "green" ? "#16a34a" : "#0b2960" }}>
                        <div className="text-white">
                          <div className="text-sm font-extrabold tracking-wide">{section.title}</div>
                          <div className="text-[11px] opacity-80">{section.subtitle}</div>
                        </div>
                        <div className="bg-white/95 rounded-full px-2.5 py-0.5 text-xs font-bold" style={{ color: section.color === "green" ? "#16a34a" : "#0b2960" }}>
                          {list.length} article{list.length > 1 ? "s" : ""}
                        </div>
                      </div>
                      {list.length === 0 ? (
                        <div className="p-6 text-center text-sm text-neutral-500 bg-neutral-50">
                          Aucun produit dans cette section. Sélectionnez "{section.key}" dans le formulaire "État" à gauche pour en ajouter.
                        </div>
                      ) : (
                        <ul className="divide-y divide-neutral-200">
                          {list.map((p) => (
                            <li key={p.id} className="py-3 px-4 flex items-center gap-3">
                              <div className="h-16 w-16 shrink-0 rounded-md overflow-hidden bg-neutral-100 border border-neutral-200">
                                {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-neutral-300"><ImageIcon className="h-6 w-6" /></div>}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">{p.brand || "Sans marque"} · {p.category}</div>
                                <div className="text-sm font-semibold text-neutral-800 truncate">{p.name}</div>
                                <div className="text-xs text-neutral-500 mt-0.5">
                                  <b className="text-[#e11d24]">{parseFloat(p.price).toFixed(2)} €</b>
                                  {p.oldPrice > p.price && <span className="ml-2 line-through text-neutral-400">{parseFloat(p.oldPrice).toFixed(2)} €</span>}
                                  <span className="ml-3">Stock : {p.stock}</span>
                                </div>
                              </div>
                              <button onClick={() => remove(p.id)} className="text-neutral-400 hover:text-[#e11d24] p-2" aria-label="Supprimer">
                                <Trash2 className="h-5 w-5" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
